import Decimal from 'decimal.js';

export function calculateInvestmentGrowth(data) {
  const {
    initialDeposit,     // 本金
    monthlyContribution, // 每月定投金额
    timeFrame,          // 时间长度
    apy,               // 年化收益率
    timeUnit           // 时间单位（年/月）
  } = data;

  // 设置 Decimal 的精度和舍入模式
  Decimal.set({ 
    precision: 20,
    rounding: Decimal.ROUND_HALF_UP  // 四舍五入
  });

  // 转换为 Decimal 类型
  const principal = new Decimal(initialDeposit);
  const rate = new Decimal(apy).div(100).toDecimalPlaces(6);  // 转换为小数形式
  const monthlyRate = rate.div(12).toDecimalPlaces(6);        // 月利率
  const compoundingPeriodsPerYear = 12;    // 每年复利次数（按月复利）
  const totalMonths = timeUnit === 'years' ? timeFrame * 12 : timeFrame;

  // 计算本金的复利增长
  // A = P(1 + r/n)^(nt)
  const principalGrowth = principal.mul(
    Decimal.pow(
      monthlyRate.add(1),
      totalMonths
    )
  ).toDecimalPlaces(6);

  // 计算每月定投的复利增长
  // PMT * (((1 + r/n)^(nt) - 1) / (r/n))
  const monthlyAmount = new Decimal(monthlyContribution);
  const contributionsGrowth = monthlyAmount.mul(
    Decimal.pow(monthlyRate.add(1), totalMonths)
    .sub(1)
    .div(monthlyRate)
  ).toDecimalPlaces(6);

  // 计算总投入
  const totalContributions = initialDeposit + (monthlyContribution * totalMonths);

  // 计算最终总额（最后保留2位小数）
  const finalAmount = principalGrowth.add(contributionsGrowth).toDecimalPlaces(2);

  // 计算实际年化收益率 (Effective Annual Yield)（最后保留2位小数）
  // EAY = (1 + r/n)^n - 1
  const effectiveAnnualYield = Decimal.pow(
    monthlyRate.add(1),
    compoundingPeriodsPerYear
  ).sub(1).mul(100).toDecimalPlaces(2);

  return {
    total: Math.floor(finalAmount.toNumber()),             // 总金额
    totalInterest: Math.floor(finalAmount.sub(totalContributions).toNumber()),   // 总收益
    totalContributions: Math.floor(totalContributions),    // 总投入
    effectiveAnnualYield: effectiveAnnualYield.toString(), // 实际年化收益率
    principalGrowth: Math.floor(principalGrowth.toDecimalPlaces(2).toNumber()),  // 本金增长
    contributionsGrowth: Math.floor(contributionsGrowth.toDecimalPlaces(2).toNumber())  // 定投增长
  };
}

// 用于图表显示的计算函数
export function calculateYearlyData(data) {
  const {
    initialDeposit,
    monthlyContribution,
    timeFrame,
    apy,
    timeUnit
  } = data;

  Decimal.set({ 
    precision: 20,
    rounding: Decimal.ROUND_HALF_UP
  });
  
  const rate = new Decimal(apy).div(100).toDecimalPlaces(6);
  const monthlyRate = rate.div(12).toDecimalPlaces(6);
  const totalMonths = timeUnit === 'years' ? timeFrame * 12 : timeFrame;
  
  let balance = new Decimal(initialDeposit);
  const monthlyAmount = new Decimal(monthlyContribution);
  let totalContributions = 0;
  const results = [];

  // 添加初始年（第0年）的数据
  results.push({
    period: 0,
    balance: initialDeposit,
    contributions: 0,
    interest: 0,
    effectiveYield: "0.00"
  });

  let currentPeriod = 0;
  let monthsInCurrentPeriod = 0;
  
  // 逐月计算
  for (let month = 1; month <= totalMonths; month++) {
    // 计算当月利息（保留6位小数）
    const monthlyInterest = balance.mul(monthlyRate).toDecimalPlaces(6);
    balance = balance.add(monthlyInterest).toDecimalPlaces(6);
    
    // 加入每月定投
    balance = balance.add(monthlyAmount).toDecimalPlaces(6);
    totalContributions += monthlyContribution;
    
    monthsInCurrentPeriod++;
    
    // 每12个月（或在月度模式下每个月）保存一次数据
    const periodEnd = timeUnit === 'years' ? monthsInCurrentPeriod === 12 : true;
    if (periodEnd) {
      currentPeriod++;
      monthsInCurrentPeriod = 0;

      // 计算当期实际收益率（最后保留2位小数）
      const periodYield = balance
        .sub(new Decimal(initialDeposit).add(totalContributions))
        .div(new Decimal(initialDeposit).add(totalContributions))
        .mul(100)
        .toDecimalPlaces(2);
      
      // 最终结果保留2位小数
      const finalBalance = balance.toDecimalPlaces(2);
      
      results.push({
        period: currentPeriod,
        balance: Math.floor(finalBalance.toNumber()),
        contributions: Math.floor(totalContributions),
        interest: Math.floor(finalBalance.sub(new Decimal(initialDeposit).add(totalContributions)).toNumber()),
        effectiveYield: periodYield.toString()
      });
    }
  }
  
  return results;
} 